import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'motion/react';

interface StatCounterProps {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  icon?: React.ReactNode;
}

export const StatCounter: React.FC<StatCounterProps> = ({ value, prefix = '', suffix = '', label, icon }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.floor(latest));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      const controls = animate(count, value, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    } else {
      count.set(value);
      setDisplayValue(value);
    }
  }, [isInView, value, count]);

  useEffect(() => {
    return rounded.on('change', (v) => setDisplayValue(v));
  }, [rounded]);

  return (
    <div ref={ref} className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center flex flex-col items-center justify-center">
      {icon && (
        <div className="w-12 h-12 rounded-2xl bg-blue-50 text-[#002366] flex items-center justify-center mb-3 border border-blue-100 shadow-sm">
          {icon}
        </div>
      )}
      <div className="font-heading text-3xl sm:text-4xl font-extrabold text-[#002366] tracking-tight">
        {prefix}
        {displayValue.toLocaleString()}
        {suffix}
      </div>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1.5">
        {label}
      </div>
    </div>
  );
};
